import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, 
  CheckCircle2, 
  ArrowLeft,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_OPTIONS = [
  {
    id: 'one_time',
    title: 'One-Time Payment',
    amount: '$2,500',
    description: 'Pay in full today',
    icon: DollarSign,
    url: '',
    highlight: true
  }
];

export default function PaymentPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleSelectPayment = async (option) => {
    setSelectedOption(option);
    
    try {
      await axios.post(`${API}/intake/${intakeId}/payment`, {
        intake_id: intakeId,
        payment_option: option.id
      });
    } catch (error) {
      console.error('Error selecting payment:', error);
    }
  };

  const handleProceedToPayment = () => {
    if (selectedOption) {
      window.open(selectedOption.url, '_blank');
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentReference.trim()) {
      toast.error('Please enter your payment confirmation number');
      return;
    }

    setIsConfirming(true);
    try {
      await axios.post(`${API}/intake/${intakeId}/confirm-payment?payment_reference=${encodeURIComponent(paymentReference)}`);
      
      // Create Google Drive folder
      const intakeResponse = await axios.get(`${API}/intake/${intakeId}`);
      await axios.post(`${API}/google-drive/create`, {
        intake_id: intakeId,
        form_data: intakeResponse.data
      });

      setIsComplete(true);
      toast.success('Payment confirmed! Your intake has been submitted.');
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error('Failed to confirm payment. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="payment-complete">
        <div className="max-w-md text-center p-8">
          <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-6" />
          <h1 
            className="text-3xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Thank You!
          </h1>
          <p className="text-muted-foreground mb-6">
            Your intake form has been submitted and your payment has been confirmed.
            You will receive a confirmation email shortly.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            A member of our team will contact you within 24 hours to discuss your case.
          </p>
          <div className="space-y-3">
            <p className="text-sm font-medium">Contact Information:</p>
            <p className="text-sm text-muted-foreground">
              Call Todd: <a href="tel:970-710-9193" className="underline">970-710-9193</a>
            </p>
            <p className="text-sm text-muted-foreground">
              Text Christine (Accounts): <a href="sms:970-846-3088" className="underline">970-846-3088</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="payment-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-black/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="rounded-none"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 
              className="text-xl font-bold tracking-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              ToddProffit
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Select Payment Option
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose your preferred payment plan for the initial case review deposit.
            </p>
          </div>

          {/* Payment Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {PAYMENT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedOption?.id === option.id;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelectPayment(option)}
                  className={`
                    p-6 border text-left transition-all duration-200
                    ${isSelected 
                      ? 'border-black bg-gray-50 ring-2 ring-black' 
                      : 'border-gray-200 hover:border-black'
                    }
                    ${option.highlight ? 'relative' : ''}
                  `}
                  data-testid={`payment-option-${option.id}`}
                >
                  {option.highlight && (
                    <span className="absolute -top-3 left-4 bg-black text-white text-xs px-3 py-1 uppercase tracking-wider">
                      Recommended
                    </span>
                  )}
                  
                  <Icon className="w-8 h-8 mb-4" />
                  
                  <h3 
                    className="text-xl font-semibold mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {option.title}
                  </h3>
                  
                  <p className="text-2xl font-bold mb-1">{option.amount}</p>
                  
                  {option.totalAmount && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {option.totalAmount}
                    </p>
                  )}
                  
                  <p className="text-sm text-muted-foreground">
                    {option.description}
                  </p>
                  
                  {isSelected && (
                    <div className="mt-4 flex items-center gap-2 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Payment Action Section */}
          {selectedOption && (
            <div className="max-w-xl mx-auto space-y-8">
              <Separator />
              
              {/* Step 1: Make Payment */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-black text-white text-sm font-bold">
                    1
                  </span>
                  <h3 className="text-lg font-semibold">Make Your Payment</h3>
                </div>
                
                <p className="text-muted-foreground ml-11">
                  Click the button below to proceed to secure payment via Stripe.
                </p>
                
                <div className="ml-11">
                  <Button
                    onClick={handleProceedToPayment}
                    className="rounded-none bg-black text-white hover:bg-black/90 px-8 py-6 text-lg"
                    data-testid="proceed-payment-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay {selectedOption.amount}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Step 2: Confirm Payment */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-black text-white text-sm font-bold">
                    2
                  </span>
                  <h3 className="text-lg font-semibold">Confirm Your Payment</h3>
                </div>
                
                <p className="text-muted-foreground ml-11">
                  After completing payment, enter your confirmation number below.
                </p>
                
                <div className="ml-11 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment-ref">Payment Confirmation Number</Label>
                    <Input
                      id="payment-ref"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="Enter confirmation number from Stripe"
                      className="rounded-none"
                      data-testid="payment-reference-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={!paymentReference.trim() || isConfirming}
                    className="rounded-none bg-green-600 text-white hover:bg-green-700"
                    data-testid="confirm-payment-btn"
                  >
                    {isConfirming ? 'Confirming...' : 'Confirm Payment & Submit'}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Help Text */}
              <div className="bg-gray-50 p-6 border border-gray-200">
                <h4 className="font-semibold mb-2">Need Help?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  If you have any questions about payment or need assistance, please contact us:
                </p>
                <ul className="text-sm space-y-2">
                  <li>
                    <strong>Call Todd:</strong>{' '}
                    <a href="tel:970-710-9193" className="underline">970-710-9193</a>
                  </li>
                  <li>
                    <strong>Text Christine (Accounts):</strong>{' '}
                    <a href="sms:970-846-3088" className="underline">970-846-3088</a>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Quantum LAW Consulting LLC. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Page, Automations & Ai Voice Agent- Designed, Coded and Maintained by UNLIMITED, LLC.
          </p>
        </div>
      </footer>
    </div>
  );
}
